import { Op } from "../sync/ws-envelope";
import { SyncTableMeta, SERVER_ONLY_COLUMNS } from "./sync-tables";

/** Positional-`?` SQL + bind values, ready for the loopback runUser path. */
export interface PreparedSql {
  sql: string;
  values: unknown[];
}

const SERVER_ONLY = new Set<string>(SERVER_ONLY_COLUMNS);

/**
 * Build the UPSERT that applies a remote op's full row into the local table.
 * PURE. The written column set = the op row's keys that (a) exist in the live
 * local schema (`localColumns`, from PRAGMA table_info) and (b) are not
 * server-only — so `serverSeq`/`originDeviceId`/`createdBy` and any schema skew
 * are dropped rather than erroring. `op.rowId` is authoritative for the pk
 * column; `myRegistrationId` is authoritative for the `registrationId` column on
 * tenant-scoped tables (the device is single-tenant locally, and the broadcast
 * may omit/blank it — binding the row's value would let it land under SQLite's
 * `DEFAULT 'legacy-device'`, i.e. the wrong tenant). The caller gates every apply
 * through `classifyRemoteOp` first; the SQL repeats the last-write-wins check,
 * because a local write can land after the caller read its snapshot.
 *
 * Throws a clear schema-mismatch error if the live local schema is missing a
 * column required to form a valid statement (the pk, or `registrationId` for a
 * tenant-scoped table). Tolerating schema skew means dropping *unknown* columns,
 * not silently emitting structurally-invalid SQL (e.g. `INSERT () VALUES ()`)
 * when the schema lacks everything — that would be a runtime SQLite parse error.
 */
export function buildApplyUpsert(
  op: Op,
  meta: SyncTableMeta,
  localColumns: Set<string>,
  myRegistrationId: string
): PreparedSql {
  const row = op.row as Record<string, unknown>;
  const cols = Object.keys(row).filter((c) => localColumns.has(c) && !SERVER_ONLY.has(c));
  // The pk must always be written (op.rowId is its canonical value), even if the
  // payload happened to omit it.
  if (!cols.includes(meta.pk) && localColumns.has(meta.pk)) {
    cols.push(meta.pk);
  }
  // For tenant-scoped tables, registrationId is part of the conflict target and
  // must always be written. Bind the device's own reg (single-tenant locally) so
  // a server-omitted/blank reg never falls through to SQLite's DEFAULT tenant.
  if (meta.tenantScopedPk && !cols.includes("registrationId") && localColumns.has("registrationId")) {
    cols.push("registrationId");
  }

  // Required columns must exist in the live local schema; otherwise the generated
  // statement is structurally invalid (missing conflict target / empty column
  // list). Fail loud rather than emit broken SQL.
  const requiredCols = meta.tenantScopedPk ? [meta.pk, "registrationId"] : [meta.pk];
  const missing = requiredCols.filter((c) => !localColumns.has(c));
  if (missing.length > 0) {
    throw new Error(
      `sync apply: local table \`${meta.table}\` is missing required column(s) [${missing.join(", ")}] ` +
        `(local schema has [${[...localColumns].join(", ")}]) — cannot build a valid upsert`
    );
  }

  const conflictCols = meta.tenantScopedPk ? ["registrationId", meta.pk] : [meta.pk];
  const conflictSet = new Set(conflictCols);
  const setCols = cols.filter((c) => !conflictSet.has(c));

  const values = cols.map((c) => {
    if (c === meta.pk) return op.rowId;
    if (c === "registrationId" && meta.tenantScopedPk) return myRegistrationId;
    return row[c];
  });

  // The caller's snapshot can be older than the row by now (a local write runs
  // beside the apply), so the update itself keeps a newer local row. Known gap:
  // a local write stores its row first and its updatedAt in a later request
  // (DatabaseService.appendOplog), and a peer row in between still wins.
  const newerOnly = setCols.includes("updatedAt")
    ? ` WHERE excluded.\`updatedAt\` > COALESCE(\`${meta.table}\`.\`updatedAt\`, 0)`
    : "";
  const onConflict =
    setCols.length > 0
      ? `DO UPDATE SET ${setCols.map((c) => `\`${c}\` = excluded.\`${c}\``).join(", ")}${newerOnly}`
      : "DO NOTHING";

  const sql =
    `INSERT INTO \`${meta.table}\` (${cols.map((c) => `\`${c}\``).join(", ")}) ` +
    `VALUES (${cols.map(() => "?").join(", ")}) ` +
    `ON CONFLICT(${conflictCols.join(", ")}) ${onConflict}`;

  return { sql, values };
}

/**
 * Build the UPDATE that applies a remote tombstone (obsolete/delete) into the
 * local table: set the flag column to 1 and stamp the op's updatedAt. PURE.
 * `delete` sets `deleted`, `obsolete` sets `obsolete` (the caller routes by
 * op.type against the table's capability flags). For a tenant-scoped pk the
 * WHERE also binds the device's own registrationId (the tombstone broadcast
 * omits it; the device is single-tenant locally). The caller gates LWW, and the
 * WHERE repeats the check.
 */
export function buildApplyTombstone(op: Op, meta: SyncTableMeta, myRegistrationId: string): PreparedSql {
  const flagCol = op.type === "delete" ? "deleted" : "obsolete";
  const updatedAt = Number((op.row as Record<string, unknown>)["updatedAt"]);

  const where = meta.tenantScopedPk ? `\`${meta.pk}\` = ? AND \`registrationId\` = ?` : `\`${meta.pk}\` = ?`;

  const values: unknown[] = [updatedAt, op.rowId];
  if (meta.tenantScopedPk) {
    values.push(myRegistrationId);
  }
  // Same guard as the upsert: a newer local row stays.
  values.push(updatedAt);

  const sql =
    `UPDATE \`${meta.table}\` SET \`${flagCol}\` = 1, \`updatedAt\` = ? ` +
    `WHERE ${where} AND COALESCE(\`updatedAt\`, 0) < ?`;
  return { sql, values };
}
